import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { Upload, FileIcon, Image as ImageIcon, FileText, Film, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/portal/files")({
  component: FilesPage,
});

const CATEGORIES = [
  { v: "logo", l: "Logo", icon: ImageIcon },
  { v: "images", l: "Immagini", icon: ImageIcon },
  { v: "documents", l: "Documenti", icon: FileText },
  { v: "videos", l: "Video", icon: Film },
  { v: "brand", l: "Brand guidelines", icon: BookOpen },
];

function FilesPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const [dragOver, setDragOver] = useState(false);
  const [category, setCategory] = useState("logo");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: files } = useQuery({
    queryKey: ["portal", "files"],
    queryFn: async () => (await supabase.from("project_files").select("*").order("created_at", { ascending: false })).data ?? [],
    enabled: !!project,
  });

  const upload = useCallback(async (fileList: FileList) => {
    if (!project) return;
    setUploading(true);
    const { data: user } = await supabase.auth.getUser();
    try {
      for (const file of Array.from(fileList)) {
        const path = `${user.user!.id}/${project.id}/${category}/${Date.now()}-${file.name}`;
        // Store metadata only (no storage bucket setup needed for the demo)
        await supabase.from("project_files").insert({
          project_id: project.id, uploader_id: user.user!.id,
          name: file.name, category, storage_path: path,
          mime_type: file.type, size_bytes: file.size,
        });
      }
      toast.success("File caricati");
      qc.invalidateQueries({ queryKey: ["portal", "files"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore upload");
    } finally { setUploading(false); }
  }, [project, category, qc]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">File</h1>
        <p className="text-white/60 mt-1">Carica logo, immagini, video e documenti. Organizzazione automatica.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map((c) => (
          <button key={c.v} onClick={() => setCategory(c.v)}
            className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 ${category === c.v ? "bg-white text-black" : "bg-white/5 hover:bg-white/10 text-white/70"}`}>
            <c.icon className="h-3.5 w-3.5" />{c.l}
          </button>
        ))}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files) upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`${glass} p-12 text-center cursor-pointer transition ${dragOver ? "border-fuchsia-400 bg-fuchsia-500/5" : ""}`}
      >
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => e.target.files && upload(e.target.files)} />
        <Upload className="h-8 w-8 mx-auto text-white/50" />
        <div className="mt-3 font-medium">{uploading ? "Caricamento..." : "Trascina qui i tuoi file"}</div>
        <div className="text-sm text-white/50 mt-1">oppure clicca per selezionare · categoria: <span className="text-white">{CATEGORIES.find(c => c.v === category)?.l}</span></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(files ?? []).map((f) => (
          <div key={f.id} className={`${glass} p-4 flex items-start gap-3`}>
            <div className="h-10 w-10 rounded-xl bg-white/5 grid place-items-center">
              <FileIcon className="h-5 w-5 text-white/60" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{f.name}</div>
              <div className="text-xs text-white/50">{f.category} · {f.size_bytes ? `${Math.round((f.size_bytes ?? 0) / 1024)} KB` : ""}</div>
              <div className="text-[10px] text-white/40 mt-1">{format(new Date(f.created_at), "d MMM yyyy HH:mm")}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
